import React, { useState, useEffect } from 'react';
import { getProfileStats, getDuelHistory, getRatingGraph, getFormatStats, getBestTracks, getRecentForm } from '../../api';
import HrnbCard from '../controls/HrnbCard';
import HrnbLoader from '../controls/HrnbLoader';
import HrnbAlert from '../controls/HrnbAlert';
import '../../css/duels/DuelStats.css';

/**
 * Компонент статистики дуэлей игрока
 */
function DuelStats({ profileId }) {
    const [stats, setStats] = useState(null);
    const [history, setHistory] = useState([]);
    const [ratingGraph, setRatingGraph] = useState([]);
    const [formatStats, setFormatStats] = useState([]);
    const [bestTracks, setBestTracks] = useState([]);
    const [recentForm, setRecentForm] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadStats();
    }, [profileId]);

    const loadStats = async () => {
        setLoading(true);
        setError(null);

        try {
            const [
                statsData,
                historyData,
                ratingGraphData,
                formatStatsData,
                bestTracksData,
                recentFormData
            ] = await Promise.all([
                getProfileStats(profileId),
                getDuelHistory(profileId, 10),
                getRatingGraph(profileId, 30),
                getFormatStats(profileId),
                getBestTracks(profileId, 5),
                getRecentForm(profileId, 5)
            ]);

            setStats(statsData);
            setHistory(historyData || []);
            setRatingGraph(ratingGraphData || []);
            setFormatStats(formatStatsData || []);
            setBestTracks(bestTracksData || []);
            setRecentForm(recentFormData || []);
        } catch (err) {
            console.error('[DuelStats] Failed to load stats:', err);
            setError('Не удалось загрузить статистику');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <HrnbLoader />;
    }

    if (error || !stats) {
        return (
            <HrnbCard title="📊 Статистика дуэлей">
                <HrnbAlert variant="danger">
                    {error || 'Статистика недоступна'}
                </HrnbAlert>
            </HrnbCard>
        );
    }

    const formatResult = (result) => {
        switch (result) {
            case 'WIN': return { label: 'Победа', className: 'duel-stats__result--win', icon: '✅' };
            case 'LOSS': return { label: 'Поражение', className: 'duel-stats__result--loss', icon: '❌' };
            case 'DRAW': return { label: 'Ничья', className: 'duel-stats__result--draw', icon: '🤝' };
            default: return { label: result, className: '', icon: '❓' };
        }
    };

    return (
        <div className="duel-stats">
            {/* Основная статистика */}
            <HrnbCard title="📊 Общая статистика" className="duel-stats__main">
                <div className="duel-stats__rating">
                    <div className="duel-stats__rating-value">{stats.rating}</div>
                    <div className="duel-stats__rating-label">Рейтинг</div>
                    <div className="duel-stats__rank">#{stats.rank}</div>
                </div>

                <div className="duel-stats__grid">
                    <div className="duel-stats__stat">
                        <span className="duel-stats__stat-value">{stats.wins}</span>
                        <span className="duel-stats__stat-label">Победы</span>
                    </div>
                    <div className="duel-stats__stat">
                        <span className="duel-stats__stat-value">{stats.losses}</span>
                        <span className="duel-stats__stat-label">Поражения</span>
                    </div>
                    <div className="duel-stats__stat">
                        <span className="duel-stats__stat-value">{stats.draws}</span>
                        <span className="duel-stats__stat-label">Ничьи</span>
                    </div>
                    <div className="duel-stats__stat">
                        <span className="duel-stats__stat-value">{stats.totalDuels}</span>
                        <span className="duel-stats__stat-label">Всего</span>
                    </div>
                    <div className="duel-stats__stat">
                        <span className="duel-stats__stat-value">{stats.winRate?.toFixed(1)}%</span>
                        <span className="duel-stats__stat-label">Win Rate</span>
                    </div>
                    <div className="duel-stats__stat">
                        <span className="duel-stats__stat-value">{stats.averageScore?.toFixed(1)}</span>
                        <span className="duel-stats__stat-label">Ср. балл</span>
                    </div>
                </div>

                {/* Недавняя форма */}
                {recentForm.length > 0 && (
                    <div className="duel-stats__form">
                        <span className="duel-stats__form-label">Последние игры:</span>
                        <div className="duel-stats__form-items">
                            {recentForm.map((result, index) => {
                                const formatted = formatResult(result);
                                return (
                                    <span
                                        key={index}
                                        className={`duel-stats__form-item ${formatted.className}`}
                                        title={formatted.label}
                                    >
                                        {formatted.icon}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                )}
            </HrnbCard>

            {/* Статистика по форматам */}
            {formatStats.length > 0 && (
                <HrnbCard title="📈 Статистика по форматам" className="duel-stats__formats">
                    <div className="duel-stats__formats-grid">
                        {formatStats.map((format) => (
                            <div key={format.format} className="duel-stats__format">
                                <div className="duel-stats__format-name">
                                    {format.format === 'SOLO' ? '1 на 1' : format.format}
                                </div>
                                <div className="duel-stats__format-stats">
                                    <div className="duel-stats__format-stat">
                                        <span className="duel-stats__format-stat-value">{format.wins}</span>
                                        <span className="duel-stats__format-stat-label">Победы</span>
                                    </div>
                                    <div className="duel-stats__format-stat">
                                        <span className="duel-stats__format-stat-value">{format.losses}</span>
                                        <span className="duel-stats__format-stat-label">Поражения</span>
                                    </div>
                                    <div className="duel-stats__format-stat">
                                        <span className="duel-stats__format-stat-value">{format.draws || 0}</span>
                                        <span className="duel-stats__format-stat-label">Ничьи</span>
                                    </div>
                                </div>
                                <div className="duel-stats__format-winrate">
                                    Win Rate: {format.winRate?.toFixed(1)}%
                                </div>
                            </div>
                        ))}
                    </div>
                </HrnbCard>
            )}

            {/* Лучшие треки */}
            {bestTracks.length > 0 && (
                <HrnbCard title="🎵 Лучшие треки" className="duel-stats__tracks">
                    <div className="duel-stats__tracks-list">
                        {bestTracks.map((track, index) => (
                            <div key={track.trackId} className="duel-stats__track">
                                <span className="duel-stats__track-rank">#{index + 1}</span>
                                <div className="duel-stats__track-info">
                                    <div className="duel-stats__track-title">{track.title}</div>
                                    <div className="duel-stats__track-duel">{track.duelTitle}</div>
                                </div>
                                <div className="duel-stats__track-stats">
                                    <div className="duel-stats__track-score">
                                        <span className="duel-stats__track-score-value">
                                            {track.averageScore?.toFixed(1)}
                                        </span>
                                        <span className="duel-stats__track-score-label">Ср. балл</span>
                                    </div>
                                    <div className="duel-stats__track-votes">
                                        <span className="duel-stats__track-votes-value">
                                            {track.votesCount}
                                        </span>
                                        <span className="duel-stats__track-votes-label">Голосов</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </HrnbCard>
            )}

            {/* История дуэлей */}
            {history.length > 0 && (
                <HrnbCard title="📜 История дуэлей" className="duel-stats__history">
                    <div className="duel-stats__history-list">
                        {history.map((duel) => {
                            const result = formatResult(duel.result);
                            return (
                                <div key={duel.duelId} className="duel-stats__history-item">
                                    <div className={`duel-stats__history-result ${result.className}`}>
                                        {result.icon}
                                    </div>
                                    <div className="duel-stats__history-info">
                                        <div className="duel-stats__history-title">{duel.title}</div>
                                        <div className="duel-stats__history-opponent">
                                            vs {duel.opponent?.name || 'Неизвестно'}
                                        </div>
                                    </div>
                                    <div className="duel-stats__history-stats">
                                        <div className="duel-stats__history-score">{duel.score}</div>
                                        <div className={`duel-stats__history-rating ${duel.ratingChange >= 0 ? 'positive' : 'negative'}`}>
                                            {duel.ratingChange >= 0 ? '+' : ''}{duel.ratingChange}
                                        </div>
                                    </div>
                                    <div className="duel-stats__history-date">
                                        {new Date(duel.playedAt).toLocaleDateString('ru-RU')}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </HrnbCard>
            )}
        </div>
    );
}

export default DuelStats;
